/**
 * Coups de pouce — Attendu 1123 « Reconnaître un parallélogramme particulier à
 * partir de ses propriétés caractéristiques, notamment à partir des propriétés
 * de ses diagonales » (automatisme 4e).
 *
 * nature_qcm_unique, nature_planche_qcm_unique, propriete_qcm_unique, vf_tableau_vf :
 * entrée PARTAGÉE « quel parallélogramme particulier ? » — la figure est déjà
 * un parallélogramme, le codage décide : un angle droit ou des diagonales de
 * même longueur → rectangle ; deux côtés consécutifs égaux ou des diagonales
 * perpendiculaires → losange ; les deux → carré ; sinon rien de plus. Seuls
 * les codages font foi. Exemple : EFGH, qui a l'allure d'un carré, codé
 * seulement « diagonales de même longueur » → rectangle.
 *
 * rappel_tableau_vf : pur rappel, pas d'entrée (doctrine 2026-07-31).
 * Lettres E, F, G, H, K hors du générateur (A, B, C, D, O).
 */

const E = [62, 212]; const F = [178, 212]; const G = [178, 96]; const H = [62, 96]; const K = [120, 154];
const SEG = (a, b, couleur = '#1e293b', largeur = 1.8) => `<line x1="${a[0]}" y1="${a[1]}" x2="${b[0]}" y2="${b[1]}" stroke="${couleur}" stroke-width="${largeur}"/>`;
const PT = (p, label, dx, dy, couleur = '#1e293b') => `<circle cx="${p[0]}" cy="${p[1]}" r="2.6" fill="${couleur}"/><text x="${p[0] + dx}" y="${p[1] + dy}" font-family="system-ui,sans-serif" font-size="12.5" font-weight="800" fill="${couleur}">${label}</text>`;
/** Trait d'égalité au milieu de [a b]. */
const TICK = (a, b, couleur = '#1e293b') => {
    const mx = (a[0] + b[0]) / 2; const my = (a[1] + b[1]) / 2; const dx = b[0] - a[0]; const dy = b[1] - a[1]; const n = Math.hypot(dx, dy); const nx = -dy / n * 6; const ny = dx / n * 6;
    return `<line x1="${(mx + nx).toFixed(1)}" y1="${(my + ny).toFixed(1)}" x2="${(mx - nx).toFixed(1)}" y2="${(my - ny).toFixed(1)}" stroke="${couleur}" stroke-width="2.2"/>`;
};
const FIGURE = `<polygon points="${E.join(',')} ${F.join(',')} ${G.join(',')} ${H.join(',')}" fill="#dbeafe" fill-opacity="0.5" stroke="#1e293b" stroke-width="1.8"/>`
    + SEG(E, G, '#64748b', 1.2) + SEG(F, H, '#64748b', 1.2)
    + TICK(E, K) + TICK(K, G) + TICK(F, K) + TICK(K, H)
    + PT(E, 'E', -13, 14) + PT(F, 'F', 5, 14) + PT(G, 'G', 5, -6) + PT(H, 'H', -13, -6) + PT(K, 'K', -4, 20, '#DC2626');
const T = (x, y, s, couleur, poids = 700, taille = 11) => `<text x="${x}" y="${y}" font-size="${taille}" font-weight="${poids}" fill="${couleur}">${s}</text>`;

const SVG_NATURE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 330" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Question : EFGH est un parallélogramme. D'après le codage, que peut-on affirmer de plus précis ? En 3 étapes à faire défiler avec les flèches. Étape 1 : on lit le codage, rien d'autre : les quatre demi-diagonales portent le même trait, donc les diagonales [EG] et [FH] ont la même longueur. Étape 2 : un parallélogramme dont les diagonales ont la même longueur est un rectangle. Étape 3 : rien ne dit que les diagonales sont perpendiculaires, ni que deux côtés consécutifs sont égaux : même si EFGH a l'air d'un carré, on ne peut pas l'affirmer. Réponse : EFGH est un rectangle">
  <style>@keyframes cp1123-pop { from { opacity: 0; } to { opacity: 1; } } .cp1123-pop { opacity: 0; animation: cp1123-pop 0.5s ease forwards; }</style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">On lit le codage, rien d'autre</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Quelle propriété caractéristique est codée ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">L'allure ne prouve rien</text>
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${T(8, 44, 'Question : EFGH est un parallélogramme. D\'après le codage,', '#1E3A8A')}${T(8, 58, 'que peut-on affirmer de plus précis ?', '#1E3A8A')}
    ${FIGURE}
    ${T(214, 120, 'Codage lu : les quatre', '#1E3A8A')}${T(214, 134, 'demi-diagonales portent', '#1E3A8A')}${T(214, 148, 'le même trait, donc', '#1E3A8A')}${T(214, 162, 'EG = FH : les diagonales', '#1E3A8A')}${T(214, 176, 'ont la même longueur.', '#1E3A8A')}
  </g>
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1123-pop">
    ${SEG(E, G, '#1D9E75', 3.2)}${SEG(F, H, '#1D9E75', 3.2)}
    ${T(8, 240, 'Un parallélogramme dont les diagonales ont la même longueur', '#1D9E75')}${T(8, 254, 'est un rectangle.', '#1D9E75')}
  </g></g>
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1123-pop">
    ${T(8, 274, 'Rien ne dit que les diagonales sont perpendiculaires, ni que', '#B45309')}${T(8, 288, 'deux côtés consécutifs sont égaux : même s\'il a l\'air d\'un carré,', '#B45309')}${T(8, 302, 'on ne peut pas l\'affirmer.', '#B45309')}
    ${T(8, 324, 'Réponse : EFGH est un rectangle.', '#1e293b', 700, 11.5)}
  </g></g>
</svg>`.trim();

const AIDE_NATURE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**La figure est déjà un parallélogramme.** On cherche seulement si le codage permet de dire plus : rectangle, losange, carré, ou rien de plus.',
                '**Rectangle** : un parallélogramme qui a un angle droit, ou dont les diagonales ont la même longueur.',
                '**Losange** : un parallélogramme qui a deux côtés consécutifs de même longueur, ou dont les diagonales sont perpendiculaires.',
                '**Carré** : un parallélogramme qui est à la fois un rectangle et un losange, par exemple des diagonales de même longueur et perpendiculaires.',
            ],
        },
        { titre: 'Un exemple', svg: SVG_NATURE },
    ],
};

const CP_1123 = {
    nature_qcm_unique: AIDE_NATURE,
    propriete_qcm_unique: AIDE_NATURE,
    nature_planche_qcm_unique: AIDE_NATURE,
    vf_tableau_vf: AIDE_NATURE,
};

export default CP_1123;
